import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Clock, CreditCard, Shield, CheckCircle, BarChart3 } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useProductViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import HeroVisualCard from "@/components/HeroVisualCard";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function MerchantCashAdvancePage() {
  useProductViewed("merchant_cash_advance");
  return (
    <div>
      <SEOHead title="Merchant Cash Advance Solutions" description="Grow your enterprise with flexible merchant cash advance services from CanDo Payments. Get the fast funding you need to expand, buy stock or hire staff today." path="/merchant-cash-advance" service={{ name: "Merchant Cash Advance", description: "Flexible unsecured business funding (£5,000 – £200,000) for UK merchants, repaid through a small percentage of daily card sales.", serviceType: "Business Funding" }} />
      <section className="hero-gradient py-16 md:py-24" data-testid="mca-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Merchant Cash Advance</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Flexible funding that <span className="text-brand-aqua">flexes with you</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                Access working capital without long forms, fixed repayments or slow decisions. Repay through a small percentage of your daily card sales — when trade is strong you repay faster, when it's quieter you repay less.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/contact?product=Merchant%20Cash%20Advance&category=funding&source=mca-page" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="mca-cta-quote">Check Your Eligibility <ArrowRight size={16} /></Link>
              </motion.div>
            </motion.div>
            <HeroVisualCard
              icon={TrendingUp}
              accentIcons={[Clock, BarChart3, Shield]}
              accentLabels={["Decision in 48h", "£5k – £200k", "No fixed repayments"]}
              testId="mca-hero-visual"
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, label: "Flexible Repayments" },
            { icon: Clock, label: "Quick Decisions" },
            { icon: Shield, label: "No Hidden Fees" },
            { icon: BarChart3, label: "Based on Card Sales" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
              <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
              <span className="text-sm font-semibold text-brand-black">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-testid="mca-details">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">How it works</h2>
              {/* Approved repayment wording (Gary, 4 Oct 2026, WEB-009). It replaces the
                  equivalent "instead of set monthly payments" sentence and the matching
                  checklist item, so the benefit is stated once. */}
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2" data-testid="mca-no-fixed-repayments">No fixed repayments</h3>
              <p className="text-brand-grey leading-relaxed mb-4">
                Repayments are calculated as an agreed percentage of your card sales, so the amount varies with your takings rather than being a fixed monthly instalment.
              </p>
              <p className="text-brand-grey leading-relaxed mb-6">
                There's no interest, no hidden fees and no unexpected charges — just a clear, upfront cost that stays the same from day one.
              </p>
              <div className="space-y-3">
                {["Repay automatically through card sales", "No interest — one clear upfront cost", "Minimal paperwork, quick decisions", "Funds released rapidly", "No need to change your payment setup", "Perfect for cash flow, refurbishments, stock or short-term opportunities"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                    <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Check your eligibility</h3>
                <p className="text-sm text-brand-grey mb-6">Tell us about your business and we'll let you know if a Merchant Cash Advance could work for you.</p>
                <ContactForm consentVariant="referral" />
              </div>
              <FormReviewCompanion product="merchant-cash-advance" testId="merchant-cash-advance-form-review" />
            </div>
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="merchant-cash-advance"
        eyebrow="Customer Stories"
        heading="Customer feedback on our payment services"
        subheading="Feedback about CanDo's payment services and support."
        testId="mca-related-reviews"
      />

      <section className="py-10 bg-white border-t border-brand-light-grey/50" data-testid="mca-disclaimer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-brand-grey leading-relaxed">
            CanDo Payments Ltd acts solely as an independent introducer. Finance products are provided by third-party lenders and finance providers and are subject to status, eligibility and underwriting criteria. CanDo Payments Ltd does not provide lending, credit facilities or regulated financial advice.
          </p>
        </div>
      </section>
    </div>
  );
}
